import React, { useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Spinner } from "react-bootstrap";
import Chart from "chart.js/auto";
import { ApiHook } from "../../../hooks/apiHook";

const TopProductsChart = ({ products }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!chartRef.current || !products || products.length === 0) return;

    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    const top = products.slice(0, 8);
    const colors = ["#4e73df", "#1cc88a", "#f6c23e", "#e74a3b", "#36b9cc", "#858796", "#5a5c69", "#6f42c1"];

    const ctx = chartRef.current.getContext("2d");
    chartInstance.current = new Chart(ctx, {
      type: "pie",
      data: {
        labels: top.map((p) => p.name),
        datasets: [{
          data: top.map((p) => p.totalSales),
          backgroundColor: colors.slice(0, top.length),
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: "right", labels: { font: { size: 10 }, boxWidth: 12 } },
        },
      },
    });

    return () => {
      if (chartInstance.current) chartInstance.current.destroy();
    };
  }, [products]);

  return (
    <div style={{ height: "300px" }}>
      <canvas ref={chartRef}></canvas>
    </div>
  );
};

const ProductSalesSection = ({ startDate, endDate, legId }) => {
  const { t } = useTranslation();
  const { data, isLoading, isFetching, isError } = ApiHook.CallNetworkProducts(startDate, endDate, legId);

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" size="sm" /> {t("loading")}
      </div>
    );
  }

  if (isError || !data?.data) {
    return <div className="alert alert-warning">{t("errorLoadingData")}</div>;
  }

  const { products, grandTotalSales, totalOrders, totalPv, ordersAvailable } = data.data;

  if (ordersAvailable === false || products === null) {
    return (
      <div className="alert alert-info">
        <i className="bi bi-info-circle me-2"></i>
        {t("productSalesNoOrderData")}
      </div>
    );
  }

  const formatNum = (n) => {
    if (n == null || isNaN(n)) return "0";
    return Number(n).toLocaleString();
  };

  return (
    <div>
      {isFetching && !isLoading && (
        <div className="text-center mb-2">
          <Spinner animation="border" size="sm" />
        </div>
      )}

      {/* Summary Cards */}
      <div className="row mb-3">
        <div className="col-md-4 mb-2">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body text-center py-2">
              <small className="text-muted">{t("totalOrders")}</small>
              <h5 className="mb-0" style={{ color: "#4e73df", fontWeight: "bold" }}>{formatNum(totalOrders)}</h5>
            </div>
          </div>
        </div>
        <div className="col-md-4 mb-2">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body text-center py-2">
              <small className="text-muted">{t("totalPv")}</small>
              <h5 className="mb-0" style={{ color: "#1cc88a", fontWeight: "bold" }}>{formatNum(totalPv)}</h5>
            </div>
          </div>
        </div>
        <div className="col-md-4 mb-2">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body text-center py-2">
              <small className="text-muted">{t("grandTotal")}</small>
              <h5 className="mb-0" style={{ color: "#f6c23e", fontWeight: "bold" }}>{formatNum(grandTotalSales)}</h5>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-md-5 mb-3">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body">
              <h6 className="card-title" style={{ fontSize: "14px" }}>{t("topProducts")}</h6>
              {products && products.length > 0 ? (
                <TopProductsChart products={products} />
              ) : (
                <p className="text-muted text-center py-4">{t("noDataAvailable")}</p>
              )}
            </div>
          </div>
        </div>

        <div className="col-md-7 mb-3">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body">
              <h6 className="card-title" style={{ fontSize: "14px" }}>
                {t("productSalesDetails")}
                <span className="text-muted ms-2" style={{ fontSize: "11px" }}>
                  ({t("grandTotal")}: {formatNum(grandTotalSales)})
                </span>
              </h6>
              {products && products.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-sm table-hover" style={{ fontSize: "12px" }}>
                    <thead>
                      <tr>
                        <th>{t("product")}</th>
                        <th className="text-end">{t("quantity")}</th>
                        <th className="text-end">{t("totalSales")}</th>
                        <th className="text-end">{t("percentage")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {products.map((product, idx) => (
                        <tr key={product.itemReference || idx}>
                          <td>
                            <div>{product.name}</div>
                            {product.model && (
                              <small className="text-muted">{product.model}</small>
                            )}
                          </td>
                          <td className="text-end">{product.totalQuantity}</td>
                          <td className="text-end">{formatNum(product.totalSales)}</td>
                          <td className="text-end">
                            <div className="d-flex align-items-center justify-content-end gap-1">
                              <div
                                className="progress"
                                style={{ width: "50px", height: "6px" }}
                              >
                                <div
                                  className="progress-bar"
                                  style={{
                                    width: `${product.percentage}%`,
                                    backgroundColor: "#4e73df",
                                  }}
                                ></div>
                              </div>
                              <span>{product.percentage}%</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-muted text-center py-4">{t("noDataAvailable")}</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductSalesSection;
